import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';

import { auth, db } from '../utils/firebase';

import { normalizeJob } from '../utils/normalizeJob';
import { buildKeywords } from '../utils/buildKeywords';
import { buildJobHash } from '../utils/jobHash';

const JOBS_COLLECTION = 'jobs';

/*
===============================================================================
CREATE JOB
===============================================================================
*/

export async function createJob(jobData) {

  try {

    const employerId =
      auth.currentUser?.uid;

    if (!employerId) {
      throw new Error('No authenticated employer.');
    }

    const normalizedJob =
      normalizeJob({
        ...jobData,
        source: jobData.source || 'local'
      });

    const searchKeywords =
      buildKeywords(normalizedJob);

    const hash =
      buildJobHash(normalizedJob);

    const existing =
      await findDuplicateJob(hash);

    if (existing) {

      return {
        success: false,
        duplicate: true,
        message: 'Duplicate job already exists.'
      };
    }

    const expiryDate =
      new Date();

    expiryDate.setDate(
      expiryDate.getDate() + 30
    );

    const payload = {
      ...normalizedJob,

      employerId,

      active: true,
      approved: true,

      hash,

      searchKeywords,

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),

      postedDate: Timestamp.now(),

      expiryDate:
        Timestamp.fromDate(expiryDate),

      views: 0,
      applications: 0
    };

    const docRef =
      await addDoc(
        collection(db, JOBS_COLLECTION),
        payload
      );

    return {
      success: true,
      id: docRef.id
    };

  } catch (error) {

    console.error(
      'createJob error:',
      error
    );

    return {
      success: false,
      error: error.message
    };
  }
}

/*
===============================================================================
UPDATE JOB
===============================================================================
*/

export async function updateJob(
  jobId,
  updates
) {

  try {

    const existingJob =
      await getJob(jobId);

    if (!existingJob) {
      throw new Error('Job not found.');
    }

    const normalizedUpdates =
      normalizeJob({
        ...existingJob,
        ...updates,
        source: updates.source || existingJob.source || 'local'
      });

    const searchKeywords =
      buildKeywords(normalizedUpdates);

    const hash =
      buildJobHash(normalizedUpdates);

    const ref =
      doc(db, JOBS_COLLECTION, jobId);

    await updateDoc(ref, {
      ...normalizedUpdates,

      hash,

      searchKeywords,

      updatedAt: serverTimestamp()
    });

    return {
      success: true
    };

  } catch (error) {

    console.error(
      'updateJob error:',
      error
    );

    return {
      success: false,
      error: error.message
    };
  }
}

/*
===============================================================================
DELETE JOB
===============================================================================
*/

export async function deleteJob(jobId) {

  try {

    await deleteDoc(
      doc(db, JOBS_COLLECTION, jobId)
    );

    return {
      success: true
    };

  } catch (error) {

    console.error(
      'deleteJob error:',
      error
    );

    return {
      success: false,
      error: error.message
    };
  }
}

/*
===============================================================================
GET SINGLE JOB
===============================================================================
*/

export async function getJob(jobId) {

  try {

    const ref =
      doc(db, JOBS_COLLECTION, jobId);

    const snapshot =
      await getDoc(ref);

    if (!snapshot.exists()) {
      return null;
    }

    return {
      id: snapshot.id,
      ...snapshot.data()
    };

  } catch (error) {

    console.error(
      'getJob error:',
      error
    );

    return null;
  }
}

/*
===============================================================================
GET PUBLIC JOBS
===============================================================================
*/

export async function getJobs(max = 100) {

  try {

    const q = query(
      collection(db, JOBS_COLLECTION),
      where('active', '==', true),
      where('approved', '==', true),
      orderBy('postedDate', 'desc'),
      limit(max)
    );

    const snapshot =
      await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

  } catch (error) {

    console.error(
      'getJobs error:',
      error
    );

    return [];
  }
}

/*
===============================================================================
SEARCH JOBS
===============================================================================
*/

export async function searchJobs(keyword) {

  try {

    const normalizedKeyword =
      keyword
        .trim()
        .toLowerCase();

    const q = query(
      collection(db, JOBS_COLLECTION),
      where('active', '==', true),
      where('approved', '==', true),
      where(
        'searchKeywords',
        'array-contains',
        normalizedKeyword
      ),
      limit(50)
    );

    const snapshot =
      await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

  } catch (error) {

    console.error(
      'searchJobs error:',
      error
    );

    return [];
  }
}

/*
===============================================================================
GET EMPLOYER JOBS
===============================================================================
*/

export async function getEmployerJobs() {

  try {

    const employerId =
      auth.currentUser?.uid;

    if (!employerId) {
      return [];
    }

    console.log(
      'Current User:',
      auth.currentUser?.uid
    );

    const q = query(
      collection(db, JOBS_COLLECTION),
      where(
        'employerId',
        '==',
        employerId
      ),
      orderBy('createdAt', 'desc')
    );

    const snapshot =
      await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

  } catch (error) {

    console.error(
      'getEmployerJobs error:',
      error
    );

    return [];
  }
}

/*
===============================================================================
INCREMENT VIEWS
===============================================================================
*/

export async function incrementJobViews(jobId) {

  try {

    const job =
      await getJob(jobId);

    if (!job) {
      return;
    }

    await updateJob(jobId, {
      views:
        (job.views || 0) + 1
    });

  } catch (error) {

    console.error(
      'incrementJobViews error:',
      error
    );
  }
}

/*
===============================================================================
EXPIRE OLD JOBS
===============================================================================
*/

export async function expireOldJobs() {

  try {

    const now =
      Timestamp.now();

    const q = query(
      collection(db, JOBS_COLLECTION),
      where('active', '==', true),
      where('expiryDate', '<=', now)
    );

    const snapshot =
      await getDocs(q);

    const promises =
      snapshot.docs.map(docSnap => {

        return updateDoc(
          docSnap.ref,
          {
            active: false,
            updatedAt: serverTimestamp()
          }
        );
      });

    await Promise.all(promises);

    return {
      success: true,
      expired: snapshot.docs.length
    };

  } catch (error) {

    console.error(
      'expireOldJobs error:',
      error
    );

    return {
      success: false,
      error: error.message
    };
  }
}

/*
===============================================================================
DUPLICATE CHECK
===============================================================================
*/

async function findDuplicateJob(hash) {

  const q = query(
    collection(db, JOBS_COLLECTION),
    where('active', '==', true),
    where('approved', '==', true),
    where('hash', '==', hash),
    limit(1)
  );

  const snapshot =
    await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  return snapshot.docs[0].data();
}
